import React from 'react';

import { ThunderboltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useGetStockSalesTableQuery, useSetAutoSizeGridMutation } from 'services/graphql/main';
import { useError } from 'services/utils';

import { AutoGridButton } from './styles';

interface Props {
  disabled?: boolean;
  productId: number;
}

/**
 * Toggles the automatic grid. Enabled, it is a subscription: the breakdown will change every
 * night. No dialog opens, the button state says the outcome; only a refusal speaks.
 */
const AutoSizeGridButton: React.FC<Props> = ({ productId, disabled }) => {
  const { t } = useTranslation('translation');
  const { addError } = useError();

  // Same source as the summary, so no extra call: Apollo serves it from cache.
  const { data: tableData } = useGetStockSalesTableQuery({
    variables: { productId },
    onError: (err) => addError(err, 'error'),
  });

  const hasGrid = !!tableData?.stocksSalesTable?.hasAutoGrid;
  const isAdopted = !!tableData?.stocksSalesTable?.isAutoGridAdopted;

  const [toggle, { loading }] = useSetAutoSizeGridMutation({
    // A refusal arrives as an error code, translated under its « errors. » key. Shown as a
    // warning: nothing is broken, the grid simply cannot be adopted.
    onError: (err) => addError(err, 'warning'),
    // Refetch rather than remount: recreating two ag-grid tables would trigger the
    // ResizeObserver storm this application is known for.
    refetchQueries: ['GetStockSalesTable', 'GetSizeSystemRow', 'GetProductOptimalStockProductSummary'],
  });

  return (
    <AutoGridButton
      type="button"
      $active={isAdopted}
      disabled={disabled || loading || (!hasGrid && !isAdopted)}
      title={hasGrid || isAdopted ? undefined : t('productOptimalStock.auto_grid_none_yet')}
      onClick={(event) => {
        // The button lives in a collapsible panel header: without this, the click folds it.
        event.stopPropagation();
        toggle({ variables: { productId, enabled: !isAdopted } });
      }}
    >
      <ThunderboltOutlined /> {t('productOptimalStock.auto_grid_adopt')}
    </AutoGridButton>
  );
};

export default AutoSizeGridButton;
